<script setup>

import {onMounted, ref, toRaw} from "vue";
import ScheduleApiService from "./Schedule/Services/ScheduleApiService";
import {useI18n} from "vue-i18n";
import Variable from "./Helpers/Variable";
import Spinner from "./Spinner.vue";
import List from "./Schedule/Components/List.vue";
import Search from "./Schedule/Components/Search.vue";
import ScheduleLocalFilter from "./Schedule/Services/ScheduleLocalFilter";
import FilterDTO from "./Schedule/DTO/FilterDTO";
import SortableSchedules from "./Schedule/Helpers/SortableSchedules";

const officialTabId = 'official';
const parallelTabId = 'parallel';
const officialApiStageId = ref();
const parallelApiStageId = ref();
const {t} = useI18n();
const contentLoaded = ref(false);

const eventDays = ref();
const groupedStages = ref();
const activeTab = ref(officialTabId);
const cachedOfficialSchedules = ref();
const cachedParallelSchedules = ref();
const hasAnyOfficialSchedule = ref(false);
const hasAnyParallelSchedule = ref(false);
const filteredOfficialSchedules = ref();
const filteredParallelSchedules = ref();
const hasEmptySearchResult = ref(false);
let currentFilters = ref();

onMounted(async () => {
    eventDays.value = await ScheduleApiService.getEventDays();

    if (eventDays.value === null) {
        return;
    }

    groupedStages.value = await ScheduleApiService.getStages();

    const officialStageId = groupedStages.value.getOfficialStageId();

    if (officialStageId !== null) {
        officialApiStageId.value = officialStageId;
        cachedOfficialSchedules.value = (await ScheduleApiService.getSchedules(null, officialStageId)).sort().groupByStartDate();
    }

    if (groupedStages.value.getOfficialStageId() !== null && Variable.notEmpty(cachedOfficialSchedules.value.getOriginalSchedules())) {
        hasAnyOfficialSchedule.value = true;
    }

    const parallelStageId = groupedStages.value.getParallelStageId();

    if (parallelStageId !== null) {
        parallelApiStageId.value = parallelStageId;
        cachedParallelSchedules.value = (await ScheduleApiService.getSchedules(null, parallelStageId)).sort().groupByStartDate();
    }

    if (parallelStageId !== null && Variable.notEmpty(cachedParallelSchedules.value.getOriginalSchedules())) {
        hasAnyParallelSchedule.value = true;
    }

    if (Variable.notEmpty(officialApiStageId.value)) {
        setOfficialTabDefaultFilterSchedules();
    }

    contentLoaded.value = true;
});

function setOfficialTabDefaultFilterSchedules() {
    const filterDTO = new FilterDTO(eventDays.value[0]);

    const filteredSchedules = new ScheduleLocalFilter(cachedOfficialSchedules.value, filterDTO, groupedStages.value.getOfficialStages());

    if (Variable.empty(filteredSchedules.getFilteredSchedules())) {
        hasEmptySearchResult.value = true;
        filteredOfficialSchedules.value = new SortableSchedules(filteredSchedules);
    } else {
        filteredOfficialSchedules.value = new SortableSchedules(filteredSchedules).sort().groupByStartDate();
        hasEmptySearchResult.value = false;
    }

    currentFilters.value = filterDTO;
}

function setParallelTabDefaultFilterSchedules() {
    const filterDTO = new FilterDTO(eventDays.value[0]);

    const filteredSchedules = new ScheduleLocalFilter(cachedParallelSchedules.value, filterDTO, groupedStages.value.getParallelStages());

    if (Variable.empty(filteredSchedules.getFilteredSchedules())) {
        hasEmptySearchResult.value = true;
        filteredParallelSchedules.value = new SortableSchedules(filteredSchedules);
    } else {
        hasEmptySearchResult.value = false;
        filteredParallelSchedules.value = new SortableSchedules(filteredSchedules).sort().groupByStartDate();
    }

    currentFilters.value = filterDTO;
}

function getActiveTabStageId() {
    return activeTab.value === officialTabId
        ? toRaw(groupedStages.value).getOfficialStageId()
        : toRaw(groupedStages.value).getParallelStageId();
}

function getActiveTabStages() {
    return activeTab.value === officialTabId
        ? toRaw(groupedStages.value).getOfficialStages()
        : toRaw(groupedStages.value).getParallelStages();
}

function getOfficialTabStages() {
    return toRaw(groupedStages.value).getOfficialStages();
}

function getParallelTabStages() {
    return toRaw(groupedStages.value).getParallelStages();
}

function updateActiveTab(tab_id) {
    if (eventDays.value === null || tab_id === activeTab.value) {
        return;
    }

    activeTab.value = tab_id;

    if (tab_id === officialTabId) {
        setOfficialTabDefaultFilterSchedules();
        return;
    }

    setParallelTabDefaultFilterSchedules();
}

async function applySearchFilters(filterDTO, filterSearch) {
    return (await ScheduleApiService.getSchedules(
        filterDTO.getDay(),
        getActiveTabStageId(),
        filterSearch
    ));
}

function updateActiveTabWithSchedules(schedules) {
    if (activeTab.value === officialTabId) {
        filteredOfficialSchedules.value = schedules;
        return;
    }

    filteredParallelSchedules.value = schedules;
}

async function filterSchedules(filterDTO) {
    if (Variable.notEmpty(currentFilters.value) && currentFilters.value.isEqualFilter(filterDTO)) {
        return;
    }

    contentLoaded.value = false;
    const filterSearch = filterDTO.getSearch();
    let filteredSchedules;

    if (Variable.notEmpty(filterSearch) && filterSearch !== currentFilters.value.getSearch()) {
        filteredSchedules = await applySearchFilters(filterDTO, filterSearch);
    } else {
        if (activeTab.value === officialTabId) {
            filteredSchedules = Variable.empty(filterSearch)
                ? toRaw(cachedOfficialSchedules.value)
                : toRaw(filteredOfficialSchedules.value);
        } else {
            filteredSchedules = Variable.empty(filterSearch)
                ? toRaw(cachedParallelSchedules.value)
                : toRaw(filteredParallelSchedules.value);
        }
    }

    filteredSchedules = new ScheduleLocalFilter(filteredSchedules, filterDTO, getActiveTabStages());

    if (Variable.empty(filteredSchedules.getFilteredSchedules())) {
        hasEmptySearchResult.value = true;
        updateActiveTabWithSchedules(new SortableSchedules(filteredSchedules));
    } else {
        updateActiveTabWithSchedules(new SortableSchedules(filteredSchedules).sort().groupByStartDate());
        hasEmptySearchResult.value = false;
    }

    currentFilters.value = filterDTO;
    contentLoaded.value = true;
}

function noFilteredSchedulesFound() {
    return Variable.empty(filteredOfficialSchedules.value) && Variable.empty(filteredParallelSchedules.value);
}

function noStagesFound() {
    return Variable.empty(officialApiStageId.value) && Variable.empty(parallelApiStageId.value);
}
</script>

<template>
    <section class="tabs">
        <ul v-if="officialApiStageId && parallelApiStageId" class="nav nav-tabs" role="tablist">
            <li v-if="officialApiStageId" class="nav-item" role="presentation">
                <button @click="updateActiveTab(officialTabId)" class="nav-link active" data-bs-toggle="tab"
                        data-bs-target="#official_stage"
                        type="button" role="tab" aria-controls="nav-official-stage" aria-selected="true">
                    {{ t('Palcos Oficiais') }}
                </button>
            </li>
            <li v-if="officialApiStageId && parallelApiStageId" class="nav-item" role="presentation">
                <button @click="updateActiveTab(parallelTabId)" class="nav-link" data-bs-toggle="tab"
                        data-bs-target="#parallel_stage"
                        type="button" role="tab" aria-controls="nav-parallel-stage" aria-selected="false">
                    {{ t('Palcos Paralelos') }}
                </button>
            </li>
        </ul>
        <hr v-if="officialApiStageId && parallelApiStageId"/>
        <div class="tab-content" v-if="!noFilteredSchedulesFound()">
            <div class="tab-pane fade show active" id="official_stage"
                 role="tabpanel" aria-labelledby="nav-official-tab">
                <Search v-if="Variable.notEmpty(toRaw(eventDays)) && Variable.notEmpty(toRaw(groupedStages))"
                        :stages="getOfficialTabStages()"
                        :event-days="eventDays"
                        :isDisabled="!hasAnyOfficialSchedule"
                        @filterSchedules="filterSchedules"
                />
                <List :schedules="filteredOfficialSchedules" :hasAnySchedule="hasAnyOfficialSchedule"/>
            </div>
            <div class="tab-pane fade" id="parallel_stage" role="tabpanel"
                 aria-labelledby="nav-parallel-tab">
                <Search v-if="Variable.notEmpty(toRaw(eventDays)) && Variable.notEmpty(toRaw(groupedStages))"
                        :stages="getParallelTabStages()"
                        :event-days="eventDays"
                        :isDisabled="!hasAnyParallelSchedule"
                        @filterSchedules="filterSchedules"
                />
                <List :schedules="filteredParallelSchedules" :hasAnySchedule="hasAnyParallelSchedule"/>
            </div>
        </div>
    </section>
    <Spinner v-if="eventDays !== null && contentLoaded === false && !noStagesFound() && hasEmptySearchResult === false"/>
    <h3 v-if="noStagesFound() && contentLoaded">{{ t('Disponível em breve!') }}</h3>
</template>
